import { useIsAuthenticated } from '@azure/msal-react';
import {
  rtkPermissionsClient,
  rtkUsersClient,
} from '@gm-commercial/rtk-clients';
import type { UserGrantsResponse } from '@gm-commercial/rtk-clients/permissions/types';
import type { ProfileResponse } from '@gm-commercial/rtk-clients/users/types';
import type { SerializedError } from '@reduxjs/toolkit';
import type { FetchBaseQueryError } from '@reduxjs/toolkit/query/react';
import {
  createContext,
  type ReactNode,
  useContext,
  useEffect,
  useMemo,
} from 'react';
import { useDispatch } from 'react-redux';

import { BDAppErrorType, BDError } from '~/common/models/error.model';
import {
  setPermissions,
  setPermissionsError,
  setProfile,
  setProfileError,
} from '~/store/profile/profileSlice';

import { useUserReadTelemetry } from './useUserReadTelemetry';

/** The pair of responses that make up the signed-in user. */
export type UserResponses = {
  profile?: ProfileResponse;
  permissions?: UserGrantsResponse;
};

export type UserContextValue = {
  /** The signed-in user as the API returns it; `undefined` until the request resolves. */
  profile?: ProfileResponse;
  /** The permissions granted to the signed-in user, per scope, as the API returns them. */
  permissions?: UserGrantsResponse;
  /** True until the first response of either request lands. */
  isLoading: boolean;
  /** True while either request is in flight, including background refetches. */
  isFetching: boolean;
  /** True when either request failed. */
  isError: boolean;
  /**
   * Refetches the user and their permissions, e.g. after terms are accepted,
   * and resolves with the fresh responses. Rejects when either request fails.
   */
  refetch: () => Promise<UserResponses>;
};

const UserContext = createContext<UserContextValue>({
  profile: undefined,
  permissions: undefined,
  isLoading: false,
  isFetching: false,
  isError: false,
  refetch: () => Promise.resolve({}),
});

/**
 * Converts a query error into the plain, serializable error the profile slice
 * holds, keeping the HTTP status the login flow routes on: 404 sends a user
 * with no profile to enrollment, 401 back to logout.
 *
 * Failures that never got an HTTP response (`FETCH_ERROR`, `TIMEOUT_ERROR`,
 * `PARSING_ERROR`) have no numeric status, so their sentinel goes on `code`.
 */
const toStoreError = (
  error?: FetchBaseQueryError | SerializedError
): BDError => {
  const rawStatus = error && 'status' in error ? error.status : undefined;
  const status = typeof rawStatus === 'number' ? rawStatus : undefined;
  const code =
    typeof rawStatus === 'string'
      ? rawStatus
      : error && 'code' in error
        ? error.code
        : undefined;
  const message =
    (error && 'message' in error ? error.message : undefined) ??
    'Unable to read the signed-in user';

  return BDError.asJson(
    new BDError(message, {
      status,
      code,
      type: BDAppErrorType.API,
      data: error,
    })
  );
};

/**
 * Fetches the signed-in user and their granted permissions once and shares them
 * with everything rendered under a secure route, so views and their dialogs read
 * the same user instead of each issuing their own request.
 *
 * Both are exposed exactly as the API returns them, so consumers work against
 * the API types (`ACTIVE`, `ORG_MANAGER`, ...) rather than the mapped `Profile`
 * the store holds.
 *
 * It wraps every route (see `AppContent`) rather than only the secure ones, so
 * the responses stay subscribed across navigation; both requests are skipped
 * until the user is authenticated, since the login and public routes render
 * under it too.
 *
 * Both responses are also mirrored into the profile slice, which is what the
 * `getProfile` and `getPermissions` thunks are there for today; those are being
 * retired in favour of this provider.
 */
export const UserProvider = ({
  children,
}: {
  children?: ReactNode;
}): JSX.Element => {
  const isAuthenticated = useIsAuthenticated();
  const dispatch = useDispatch();

  const {
    data: profileData,
    error: profileQueryError,
    isLoading: isProfileLoading,
    isFetching: isProfileFetching,
    isError: isProfileError,
  } = rtkUsersClient.useGet20211115MeQuery(undefined, {
    skip: !isAuthenticated,
  });

  const {
    data: permissionsData,
    error: permissionsQueryError,
    isLoading: arePermissionsLoading,
    isFetching: arePermissionsFetching,
    isError: arePermissionsError,
  } = rtkPermissionsClient.useGetMyPermissionsQuery(undefined, {
    skip: !isAuthenticated,
  });

  // The subscribed queries above only expose a fire-and-forget `refetch`, so
  // the refreshes go through lazy triggers that resolve with the response.
  const [refetchProfile] = rtkUsersClient.useLazyGet20211115MeQuery();
  const [refetchPermissions] =
    rtkPermissionsClient.useLazyGetMyPermissionsQuery();

  const profile = profileData?.result;
  const permissions = permissionsData?.result;

  // Most of the app still reads the user from the store, so both responses are
  // mirrored into it, mapped by the reducers.
  //
  // The two requests race, and the permissions are merged into the stored
  // profile that would otherwise overwrite them, so neither is mirrored until
  // both have landed and they are then written in that order. Consumers read
  // one user rather than a half-written one, and a response that arrives on its
  // own (a refetch of one of the two) still follows the same order.
  useEffect(() => {
    if (!profile || !permissions) {
      return;
    }
    dispatch(setProfile(profile));
    dispatch(setPermissions(permissions));
  }, [dispatch, profile, permissions]);

  const profileStoreError = useMemo(
    () => (isProfileError ? toStoreError(profileQueryError) : undefined),
    [isProfileError, profileQueryError]
  );
  const permissionsStoreError = useMemo(
    () =>
      arePermissionsError ? toStoreError(permissionsQueryError) : undefined,
    [arePermissionsError, permissionsQueryError]
  );

  // Failures are mirrored as well: login routes an unknown user to enrollment
  // and an expired session to logout off the status these carry.
  useEffect(() => {
    if (profileStoreError) {
      dispatch(setProfileError(profileStoreError));
    }
  }, [dispatch, profileStoreError]);

  useEffect(() => {
    if (permissionsStoreError) {
      dispatch(setPermissionsError(permissionsStoreError));
    }
  }, [dispatch, permissionsStoreError]);

  useUserReadTelemetry({
    profileError: profileStoreError,
    permissionsError: permissionsStoreError,
    isProfileFetching,
  });

  const value = useMemo(
    () => ({
      profile,
      permissions,
      isLoading: isProfileLoading || arePermissionsLoading,
      isFetching: isProfileFetching || arePermissionsFetching,
      isError: isProfileError || arePermissionsError,
      refetch: async () => {
        const [profileResponse, permissionsResponse] = await Promise.all([
          refetchProfile().unwrap(),
          refetchPermissions().unwrap(),
        ]);
        return {
          profile: profileResponse.result,
          permissions: permissionsResponse.result,
        };
      },
    }),
    [
      profile,
      permissions,
      isProfileLoading,
      arePermissionsLoading,
      isProfileFetching,
      arePermissionsFetching,
      isProfileError,
      arePermissionsError,
      refetchProfile,
      refetchPermissions,
    ]
  );

  return <UserContext.Provider value={value}>{children}</UserContext.Provider>;
};

/**
 * Reads the signed-in user, their permissions, and the state of the requests
 * that load them.
 *
 * Falls back to an unresolved value (no user, not loading, not failed) when no
 * provider is mounted, so consumers that gate on the user stay hidden rather
 * than crashing.
 * @returns The signed-in user, their permissions, and request state.
 */
export const useUser = (): UserContextValue => useContext(UserContext);
